import { CommonModule, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  computed,
  inject,
  signal,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';

const MATOMO_CATEGORY = 'WolfPack Distribution Story';

/** Where the funnel assets live under apps/distribution/src/assets. */
const ASSET_BASE = '/assets/funnel/story';

/** The prototype's design viewport. Every scene is composed against exactly this box. */
const DESIGN_W = 393;
const DESIGN_H = 852;

/** Google-hosted display faces the prototype was designed in. */
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800&family=Saira+Condensed:wght@600;700;800&display=swap';
const FONT_LINK_ID = 'wpd-story-fonts';

/**
 * Display lines that must stay inside the 393px design box in every language.
 *
 * The deck is typeset at a fixed width, so a headline that fits in English ("Multipliers.") can
 * overflow in another language — and these are single words with no break opportunity, so
 * wrapping does not save them. Each of these is shrunk to fit after the fonts load and after
 * every language switch; see fitText().
 */
const FIT_SELECTORS = [
  '.s1copy .hl', '.subline', '.s2big', '.s2sub .subtxt', '.ey', '.s3big', '.s3sub', '.pill',
  '.s4big', '.s4dep', '.s5captxt', '.s6no', '.s6plat', '.s7top .l0', '.s7top .l', '.s7fm',
  '.s9 .big', '.getstarted', '.s9 .mid'
].join(',');

interface SceneState {
  imgs: HTMLImageElement[];
  step: number;
  timers: number[];
}

interface LanguageOption {
  code: string;
  label: string;
  name: string;
}

/**
 * WolfPack Distribution story funnel — the cinematic pre-sell deck served at
 * `/r`, `/r/:sponsorId` and `/r/:lang/:sponsorId`.
 *
 * Ported from the standalone marketing prototype (import/wolfpack-distribution.com-funnel).
 * Three things about the port are load-bearing:
 *
 *  1. **The deck runs at a fixed 393x852 design size, scaled to fit.** The prototype achieved
 *     that by rendering itself inside an iframe of exactly that size; here the same box is a
 *     plain element with a `transform: scale()`. See the stylesheet for the unit substitutions
 *     that keeps every `vw`-derived size resolving against the design box rather than the device.
 *
 *  2. **ViewEncapsulation.None.** The per-scene frame `<img>` elements are created imperatively
 *     (the choreography needs direct class/transition control, including a reflow-forced
 *     "reset without fading"), so Angular would never stamp its encapsulation attribute on them
 *     and `.stage img` would not match. Every selector in the stylesheet is scoped under
 *     `.wpd-story` by hand instead.
 *
 *  3. **The last screen is a CTA into the existing lead-capture page.** Everything before it is
 *     marketing; the funnel's actual entry point stays `/:lang/r/:sponsorId`
 *     (FunnelLandingPageComponent), which this page navigates to with the sponsor and language
 *     it was opened with.
 */
@Component({
  selector: 'app-funnel-story-page',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './funnel-story-page.component.html',
  styleUrls: ['./funnel-story-page.component.scss', './funnel-story-scenes.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelStoryPageComponent implements AfterViewInit, OnDestroy {
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private location   = inject(Location);
  private i18n       = inject(I18nService);
  private translate  = inject(TranslateService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('stageBox') private stageBox!: ElementRef<HTMLElement>;
  @ViewChild('deckWrap') private deckWrap!: ElementRef<HTMLElement>;
  @ViewChild('bridge')   private bridgeEl!: ElementRef<HTMLImageElement>;
  @ViewChild('ctaLabel') private ctaLabel?: ElementRef<HTMLElement>;

  /** sponsorId from /r/:sponsorId — null means "no referrer". Always numeric (see Funnel::SponsorResolver). */
  private sponsorId: string | null = null;

  readonly desktop    = signal(this.isDesktop());
  readonly langOpen   = signal(false);
  readonly currentLang = signal<string>('en');
  readonly languages  = signal<LanguageOption[]>([]);

  readonly currentLangLabel = computed(() => {
    const code = this.currentLang();
    return this.languages().find(l => l.code === code)?.label ?? code.toUpperCase();
  });

  private scenes: HTMLElement[] = [];
  private state = new WeakMap<HTMLElement, SceneState>();
  private current = 0;
  private locked = false;
  private s1Started = false;
  private s1Revealed = false;
  private s1Timers: number[] = [];
  private listeners = new AbortController();
  private textObserver?: MutationObserver;
  private refitTimer?: number;

  constructor() {
    this.resolveRouteParams();
    this.buildLanguageOptions();
    this.ensureFonts();

    this.translate.langChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lang => this.currentLang.set(lang));
  }

  // ── route / language ────────────────────────────────────────────────────

  /**
   * `/r/:lang/:sponsorId` and `/r/:sponsorId` differ only in segment count, so a single
   * segment is ambiguous on its face: `/r/de` is a language, `/r/1042` a sponsor. Sponsor ids
   * are always numeric user ids (Funnel::SponsorResolver), so a segment that matches a
   * configured language code can only be a language.
   */
  private resolveRouteParams(): void {
    const params = this.route.snapshot.params;
    let lang: string | null = params['lang'] ?? null;
    let sponsor: string | null = params['sponsorId'] ?? null;

    if (!lang && sponsor && this.matchLanguage(sponsor)) {
      lang = sponsor;
      sponsor = null;
    }

    if (sponsor && sponsor !== '0') {
      this.sponsorId = sponsor;
      // Mirrors FunnelLandingPageComponent: the referrer must survive a reload or a
      // back-navigation that loses the /r/:sponsorId path.
      try { sessionStorage.setItem('distribution.funnel.sponsor_id', sponsor); } catch { /* ignore */ }
    } else {
      try { this.sponsorId = sessionStorage.getItem('distribution.funnel.sponsor_id'); } catch { /* ignore */ }
    }

    const resolved = (lang && this.matchLanguage(lang)) || this.i18n.getLanguage() || 'en';
    this.i18n.setLanguage(resolved, false); // no navigation: /r is not language-prefixed
    this.currentLang.set(resolved);
  }

  /** Case-insensitive lookup against the domain's configured languages ('es-MX' vs '/r/es-mx'). */
  private matchLanguage(value: string): string | null {
    const wanted = value.toLowerCase();
    return this.availableLangs().find(code => code.toLowerCase() === wanted) ?? null;
  }

  private availableLangs(): string[] {
    const langs = this.translate.getAvailableLangs();
    return langs?.length ? langs : ['en'];
  }

  private buildLanguageOptions(): void {
    this.languages.set(
      this.availableLangs().map(code => ({
        code,
        label: code.toUpperCase(),
        name: this.i18n.getLanguageName(code)
      }))
    );
  }

  toggleLangMenu(event: Event): void {
    event.stopPropagation();
    this.langOpen.update(open => !open);
  }

  selectLanguage(code: string): void {
    this.langOpen.set(false);
    if (code === this.currentLang()) return;

    this.i18n.setLanguage(code, false);
    this.currentLang.set(code);
    // Reflect the choice in the URL without re-navigating: a router navigation would
    // recreate the component and throw the viewer back to scene 1.
    this.location.replaceState(this.storyUrl(code));
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'language_switch', name: code });
  }

  private storyUrl(lang: string): string {
    return this.sponsorId ? `/r/${lang}/${this.sponsorId}` : `/r/${lang}`;
  }

  /** Hand off to the existing lead-capture page — the funnel's real first step. */
  goToApplication(): void {
    this.matomo.trackEvent({
      category: MATOMO_CATEGORY,
      action:   'cta_click',
      name:     this.sponsorId ? 'with_sponsor' : 'no_sponsor'
    });
    const lang = this.currentLang() || 'en';
    const segments = ['/', lang, 'r', ...(this.sponsorId ? [this.sponsorId] : [])];
    this.router.navigate(segments);
  }

  // ── template helpers ────────────────────────────────────────────────────

  asset(file: string): string {
    return `${ASSET_BASE}/${file}`;
  }

  /**
   * Split a translated string on "|". Headlines here are typeset as several separately
   * animated (and separately coloured) parts, and where those breaks fall differs per
   * language — so the translator owns them, not the template.
   */
  lines(value: string | null | undefined): string[] {
    if (!value) return [];
    return value.split('|').map(part => part.trim()).filter(part => part.length > 0);
  }

  // ── deck ────────────────────────────────────────────────────────────────

  ngAfterViewInit(): void {
    const root = this.deckWrap.nativeElement;
    this.scenes = Array.from(root.querySelectorAll<HTMLElement>('.scene'));

    const s1 = this.scenes[0];
    const s1imgs = this.buildFrames(s1);
    s1imgs[0]?.classList.add('f0');
    if (s1imgs[1]) {
      s1imgs[1].classList.add('f1');
      s1imgs[1].decode?.().catch(() => { /* ignore */ });
    }

    this.scenes.forEach(scene => {
      if (scene === s1) return;
      this.state.set(scene, {
        imgs: this.buildFrames(scene),
        step: parseInt(scene.getAttribute('data-step') || '250', 10),
        timers: []
      });
    });

    this.scenes[0]?.classList.add('active');

    this.bindGestures();
    this.fit();
    this.s1Start();
    // Font metrics decide whether a line fits, so measure once more when the display faces
    // arrive. Falls back to measuring immediately where document.fonts is unavailable.
    const measure = () => { this.fitText(); this.fitCtaLabel(); };
    (document.fonts?.ready ?? Promise.resolve()).then(measure).catch(measure);
    measure();
    this.watchForTextChanges();
    this.matomo.trackEvent({ category: MATOMO_CATEGORY, action: 'scene_view', name: 'scene_1', value: 1 });
  }

  ngOnDestroy(): void {
    this.listeners.abort();
    this.textObserver?.disconnect();
    clearTimeout(this.refitTimer);
    this.s1Timers.forEach(clearTimeout);
    this.scenes.forEach(scene => this.state.get(scene)?.timers.forEach(clearTimeout));
  }

  private buildFrames(scene: HTMLElement): HTMLImageElement[] {
    const stage = scene.querySelector<HTMLElement>('.stage');
    const names = (scene.getAttribute('data-frames') || '').split(',').filter(Boolean);
    return names.map(name => {
      const img = new Image();
      img.src = this.asset(`${name}.jpg`);
      img.alt = '';
      stage?.appendChild(img);
      return img;
    });
  }

  // Scene 1 is choreographed by hand: a slow fade onto frame01, then the viewer's first
  // gesture crossfades to frame02 and steps the headline in.
  private s1Start(): void {
    if (this.s1Started) return;
    this.s1Started = true;
    const s1 = this.scenes[0];
    requestAnimationFrame(() => s1.classList.add('f1in'));
    this.s1Timers.push(window.setTimeout(() => s1.classList.add('logoin'), 1150));
    this.s1Timers.push(window.setTimeout(() => this.s1Reveal(), 9000)); // safety
  }

  private s1Reveal(): void {
    if (this.s1Revealed) return;
    this.s1Revealed = true;
    this.s1Timers.forEach(clearTimeout);
    this.s1Timers = [];

    const s1 = this.scenes[0];
    s1.classList.add('logoin');
    s1.classList.add('xfade');
    this.s1Timers.push(window.setTimeout(() => s1.classList.add('step-h1'), 2000));
    this.s1Timers.push(window.setTimeout(() => s1.classList.add('step-h2'), 2600));
    this.s1Timers.push(window.setTimeout(() => s1.classList.add('step-sub'), 3300));
  }

  private playFrames(scene: HTMLElement): void {
    const st = this.state.get(scene);
    if (!st) return;
    st.timers.forEach(clearTimeout);
    st.timers = [];
    st.imgs.forEach((img, i) => {
      if (i === 0) { img.classList.add('on'); return; }
      st.timers.push(window.setTimeout(() => img.classList.add('on'), i * st.step));
    });
  }

  /** Scene 7 pairs each "+ $50,000" line with its own leader frame. */
  private s7Play(scene: HTMLElement): void {
    const st = this.state.get(scene);
    if (!st) return;
    scene.querySelectorAll('.pl').forEach(line => line.classList.remove('on'));
    const fm = scene.querySelector<HTMLElement>('.s7fm');
    fm?.classList.remove('on');

    const lines = scene.querySelectorAll<HTMLElement>('.pl');
    st.timers.push(window.setTimeout(() => fm?.classList.add('on'), 500)); // "Find Multipliers" first (top)
    const seq = [1400, 2200, 3000, 3800];
    seq.forEach((delay, i) => {
      st.timers.push(window.setTimeout(() => {
        st.imgs[i]?.classList.add('on');
        lines[i]?.classList.add('on');
      }, delay));
    });
  }

  private resetFrames(scene: HTMLElement): void {
    const st = this.state.get(scene);
    if (!st) return;
    st.timers.forEach(clearTimeout);
    st.timers = [];
    st.imgs.forEach(img => { img.style.transition = 'none'; img.classList.remove('on'); }); // instant off, no fade
    void scene.offsetWidth;                                                                 // reflow
    st.imgs.forEach(img => { img.style.transition = ''; });
  }

  private enter(scene: HTMLElement): void {
    if (scene === this.scenes[0]) {
      scene.classList.remove('out');
      scene.classList.add('active');
      return;
    }
    this.resetFrames(scene);   // clear frames WHILE still hidden — no last-frame flash on (re)entry
    scene.classList.remove('out');
    scene.classList.add('active');

    const st = this.state.get(scene)!;
    if (scene.classList.contains('s4')) {
      st.timers.push(window.setTimeout(() => this.playFrames(scene), 1800)); // copy first, then build the tower
    } else if (scene.classList.contains('s7')) {
      this.s7Play(scene);
    } else {
      this.playFrames(scene);
    }
  }

  private go(dir: number): void {
    if (this.locked) return;
    const next = this.current + dir;
    if (next < 0 || next >= this.scenes.length) return;

    this.locked = true;
    const from = this.scenes[this.current];
    const to   = this.scenes[next];
    const fromBlack = from.getAttribute('data-bg') === 'black';
    const toBlack   = to.getAttribute('data-bg') === 'black';
    const bridge = this.bridgeEl.nativeElement;

    // OUT phase: fade to black if either side is black, else to the shared bridge shot.
    bridge.style.opacity = (fromBlack || toBlack) ? '0' : '1';
    from.classList.remove('active');
    from.classList.add('out');

    window.setTimeout(() => {
      bridge.style.opacity = toBlack ? '0' : '1';   // IN phase: bridge matches the incoming scene
      this.enter(to);
      this.current = next;
      if (dir > 0) {
        this.matomo.trackEvent({
          category: MATOMO_CATEGORY, action: 'scene_view', name: `scene_${next + 1}`, value: next + 1
        });
      }
    }, 480);
    window.setTimeout(() => { this.locked = false; }, 1150);
  }

  private handleDown(): void {
    if (this.current === 0 && !this.s1Revealed) {
      this.s1Reveal();
      this.locked = true;
      window.setTimeout(() => { this.locked = false; }, 900);
      return;
    }
    this.go(1);
  }

  private handleUp(): void {
    if (this.current === 0) return;
    this.go(-1);
  }

  private bindGestures(): void {
    const abortSignal = this.listeners.signal;
    const wrap = this.deckWrap.nativeElement;
    let accum = 0;

    wrap.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      if (this.locked) { accum = 0; return; }
      accum += e.deltaY;
      if (accum > 40)       { accum = 0; this.handleDown(); }
      else if (accum < -40) { accum = 0; this.handleUp(); }
    }, { passive: false, signal: abortSignal });

    let touchY: number | null = null;
    wrap.addEventListener('touchstart', (e: TouchEvent) => { touchY = e.touches[0].clientY; }, { passive: true, signal: abortSignal });
    wrap.addEventListener('touchmove', (e: TouchEvent) => {
      if (touchY === null) return;
      const dy = e.touches[0].clientY - touchY;   // > 0 = finger swiped downward
      if (Math.abs(dy) > 36) {
        e.preventDefault();
        touchY = null;
        if (dy > 0) this.handleDown(); else this.handleUp();   // swipe down = forward
      }
    }, { passive: false, signal: abortSignal });
    wrap.addEventListener('touchend', () => { touchY = null; }, { passive: true, signal: abortSignal });

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'PageDown') { e.preventDefault(); this.handleDown(); }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp')                 { e.preventDefault(); this.handleUp(); }
    }, { signal: abortSignal });

    wrap.querySelectorAll<HTMLElement>('.cue').forEach(cue => {
      cue.style.cursor = 'pointer';
      cue.addEventListener('click', () => this.handleDown(), { signal: abortSignal });  // tapping the chevron = scroll down
    });

    window.addEventListener('resize', () => this.fit(), { signal: abortSignal });
    window.addEventListener('orientationchange', () => this.fit(), { signal: abortSignal });
    document.addEventListener('click', () => { if (this.langOpen()) this.langOpen.set(false); }, { signal: abortSignal });
  }

  // ── scaling ─────────────────────────────────────────────────────────────

  private isDesktop(): boolean {
    return window.innerWidth >= 768;
  }

  /** Scale the fixed design box to fit the viewport (phone frame on desktop, full bleed on mobile). */
  private fit(): void {
    const desktop = this.isDesktop();
    this.desktop.set(desktop);
    const availH = window.innerHeight * (desktop ? 0.84 : 1);
    const availW = window.innerWidth  * (desktop ? 0.94 : 1);
    const k = Math.min(availW / DESIGN_W, availH / DESIGN_H);
    this.stageBox.nativeElement.style.transform = `scale(${k})`;
  }

  /**
   * Re-fit whenever the deck's text actually changes.
   *
   * A language switch is not a moment we can measure at: `langChanges` fires when the active
   * language is set, but the translations for it are still being fetched, so the DOM at that
   * point still holds the previous language's words — measuring there leaves every headline
   * sized for the language the viewer just left. Watching the text itself is the only signal
   * that arrives after the words have changed. Only childList/characterData are observed, so
   * fitText's own `style` writes cannot retrigger it.
   */
  private watchForTextChanges(): void {
    this.textObserver = new MutationObserver(() => this.scheduleRefit());
    this.textObserver.observe(this.deckWrap.nativeElement, {
      subtree: true, childList: true, characterData: true
    });

    // A web font arriving is not a DOM mutation, and Google's css2 splits each family into
    // per-script @font-face rules loaded on demand — so switching to Russian first renders the
    // Cyrillic in a fallback face and only then swaps in the real one, re-laying out every line
    // after the observer has already re-measured. Without this the deck stays sized for the
    // fallback's metrics.
    document.fonts?.addEventListener?.('loadingdone', () => this.scheduleRefit(),
      { signal: this.listeners.signal });
  }

  private scheduleRefit(): void {
    clearTimeout(this.refitTimer);
    this.refitTimer = window.setTimeout(() => {
      this.fitText();
      this.fitCtaLabel();
      // Rendering the new text is what *starts* the font load for its script, so a refit can
      // easily land mid-load and measure the fallback face. Re-run once the set settles;
      // this self-corrects however many scripts the switch pulls in.
      if (document.fonts?.status === 'loading') {
        document.fonts.ready.then(() => this.scheduleRefit()).catch(() => { /* ignore */ });
      }
    }, 120);
  }

  /**
   * Shrink any display line that overflows its box until it fits.
   *
   * Cheap and bounded (a handful of elements, at most 14 steps each) and only run on load and
   * on a language switch — but it is what lets one fixed-width design carry ten languages
   * without hand-tuning a font size per locale.
   */
  private fitText(): void {
    const root = this.deckWrap?.nativeElement;
    if (!root) return;
    root.querySelectorAll<HTMLElement>(FIT_SELECTORS).forEach(el => {
      el.style.fontSize = '';   // back to the stylesheet's size before re-measuring
      const limit = this.columnWidth(el);
      let guard = 0;
      while (this.overflows(el, limit) && guard++ < 14) {
        el.style.fontSize = `${parseFloat(getComputedStyle(el).fontSize) * 0.94}px`;
      }
    });
  }

  /**
   * Two ways a headline can be too wide, and both have to be checked.
   *
   * An element with a definite width (the absolutely positioned `.s7fm`) keeps its box and lets
   * the nowrap text spill out of it — scrollWidth exceeds clientWidth. A shrink-to-fit element
   * (`display: inline-block`, like every `.hl`) does the opposite: its min-content width is the
   * longest unbreakable line, so the *box itself* grows past its column and nothing overflows
   * inside it. Checking only the first would have missed every headline on the deck.
   */
  private overflows(el: HTMLElement, limit: number): boolean {
    return el.scrollWidth > el.clientWidth + 1
        || el.getBoundingClientRect().width > limit + 1;
  }

  /**
   * Width of the design's text column for this element: the nearest positioned ancestor. Every
   * copy block in the deck is absolutely positioned with left/right insets (or is `.inner`,
   * which is relative), so that ancestor is exactly the width the composition allows — while
   * the intermediate wrappers are shrink-to-fit and would report the overflowing width itself.
   */
  private columnWidth(el: HTMLElement): number {
    let node = el.parentElement;
    while (node && node !== this.deckWrap.nativeElement) {
      if (getComputedStyle(node).position !== 'static') return node.clientWidth;
      node = node.parentElement;
    }
    return this.deckWrap.nativeElement.clientWidth;
  }

  /**
   * The CTA graphic has a fixed interior width, so a long translation ("Zarejestruj się teraz")
   * would overflow the button. Shrink it to fit rather than clipping or wrapping it.
   */
  private fitCtaLabel(): void {
    const label = this.ctaLabel?.nativeElement;
    if (!label) return;
    label.style.transform = '';
    const box = label.parentElement;
    if (!box) return;
    const available = box.clientWidth;
    const needed = label.scrollWidth;
    if (needed > available && available > 0) {
      label.style.transform = `scale(${available / needed})`;
    }
  }

  private ensureFonts(): void {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }
}
